/**
 * RecentProjectChips — вертикальный список «последних» проектов сотрудника.
 *
 * Семантика «недавних»: проекты, на которые сотрудник сам писал часы за
 * последний месяц (`RECENT_PROJECTS_WINDOW_DAYS = 30`, задача 22 §4.10).
 * Источник — self (`/field` `my_recent_projects`) и admin-ввод за сотрудника
 * (`/admin/timesheet/users/{id}/recent-projects`); порядок задаёт сервер
 * (recency, distinct), UI не пересортирует. Максимум 10 chips — backend
 * клампит (`limit=10`), UI ещё раз clamp'ит.
 *
 * Layout: vertical стэк, full-width chip'ы. Высота чипа читает density-токен
 * `--entry-tap-secondary` (как соседние контролы шага «проект» в `steps.tsx`) —
 * совпадает с окружением и на mobile (`:root` 56px), и в desktop-доке
 * (`[data-entry-surface=desktop-dock]` 40px). Горизонтальный вариант снят
 * в §4.2 — единственный потребитель (`Step2Project`) рендерит chips в столбец
 * под лейблом «Последние».
 *
 * Тап → `onPick(projectId)` → wizard зафиксирует шаг «проект».
 */
import * as React from "react";
import type { Project } from "@/api/field-timesheet";
import { cn } from "@/lib/utils";

const MAX_CHIPS = 10;

export interface RecentProjectChipsProps {
  projects: readonly Project[];
  onPick: (projectId: number) => void;
  "data-testid"?: string;
  disabled?: boolean;
}

export function RecentProjectChips({
  projects,
  onPick,
  "data-testid": testId = "recent-project-chips",
  disabled = false,
}: RecentProjectChipsProps): React.ReactElement | null {
  const visible = projects.slice(0, MAX_CHIPS).filter((p) => p.id !== null && p.id !== undefined);
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      {visible.map((p) => {
        const id = p.id as number;
        return (
          <button
            key={id}
            type="button"
            disabled={disabled}
            onClick={() => onPick(id)}
            data-testid={`${testId}-chip-${id}`}
            className={cn(
              "flex w-full items-baseline gap-3 px-3 text-left transition-[background-color,box-shadow,transform] duration-75",
              "disabled:cursor-not-allowed disabled:opacity-40 active:translate-y-[1px]",
            )}
            style={{
              minHeight: "var(--entry-tap-secondary)",
              borderRadius: "var(--radius-button)",
              background: "var(--bg-panel-0)",
              color: "var(--fg-1)",
              border: "1px solid var(--rule-line)",
              boxShadow: "var(--shadow-plate)",
            }}
          >
            <span
              className="font-condensed text-[15px] font-semibold uppercase tracking-[0.06em]"
              style={{ fontVariantNumeric: "tabular-nums", minWidth: "var(--sp-9)" }}
            >
              {p.code ?? `№ ${id}`}
            </span>
            {p.name && (
              <span
                className="min-w-0 flex-1 truncate text-[13px]"
                style={{ color: "var(--fg-3)" }}
              >
                {p.name}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
